import axios, { AxiosResponse } from 'axios'
import { User } from 'next-auth'

import { getApiUrl } from '#/shared/lib/constants'
import { IOffer } from '#/views/subscription'

interface IUserBalance {
	current_token_balance: number
}

interface IPayProductRequest {
	uid: string
	is_test: number
}

interface IPayProductResponse {
	payment_url: string
}

type PayProductResponseBody = IPayProductResponse & { detail?: string }

interface DataForLogin {
	email: string
	password: string
}

export const accountApi = {
	async getPaymentsPlans(token: string): Promise<IOffer[] | null> {
		try {
			const { data } = await axios.get<IOffer[]>(getApiUrl() + '/payments/plans', {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			return data
		} catch (err) {
			return null
		}
	},

	async payProduct(token: string | null, plan: string): Promise<string | null> {
		if (token === null) {
			return null
		}

		const response = await axios.post<IPayProductRequest, AxiosResponse<PayProductResponseBody>>(
			getApiUrl() + '/payments/plans',
			{
				uid: plan,
				is_test: 1,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)

		if (response.status >= 400) {
			const error = new Error(response.data.detail ?? 'Payment failed') as Error & {
				response: { data: PayProductResponseBody }
			}
			error.response = { data: response.data }
			throw error
		}

		return response.data.payment_url
	},

	async changePassword(
		token: string | null,
		password_1: string,
		password_2: string,
		current_password: string
	): Promise<{ status: number; data: { detail: string } }> {
		const response = await axios.put(
			getApiUrl() + '/auth/reset-pass',
			{
				password_1,
				password_2,
				current_password,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)

		return response
	},

	async getApiKeys(token: string | null): Promise<AxiosResponse> {
		return await axios.get(getApiUrl() + '/public/api-key', {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
	},

	async createApiKeys(data: any, token: string | null): Promise<AxiosResponse> {
		return await axios.post(getApiUrl() + '/public/api-key', data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
	},

	async deleteApiKey(name: any, token: string | null): Promise<AxiosResponse> {
		return await axios.delete(getApiUrl() + '/public/api-key', {
			data: {
				name,
			},
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
	},

	async loginByEmail(email: string, password: string): Promise<User | null> {
		try {
			const { data } = await axios.post<DataForLogin, AxiosResponse<User>>(getApiUrl() + '/auth/login', {
				email,
				password,
			})

			return data
		} catch (err) {
			return null
		}
	},

	async removeSub(token?: string) {
		try {
			const { status } = await axios.delete(getApiUrl() + '/payments/plans', {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			return status
		} catch (err) {
			return null
		}
	},

	revokeRecurringPayment(token: string) {
		return axios.post(
			getApiUrl() + '/payments/revoke-recurring-payment',
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)
	},
}
